import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import LogoMark from "@/components/LogoMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const baseLinks = [
  { to: "/", label: "Home", id: "home" },
  { to: "/about", label: "About", id: "about" },
  { to: "/clothing", label: "Clothing", id: "clothing" },
  { to: "/business", label: "Business", id: "business" },
  { to: "/media", label: "Media", id: "media" },
  { to: "/contact", label: "Contact", id: "contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [shopLive, setShopLive] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    fetch(`${API}/products`)
      .then((r) => r.json())
      .then((d) => setShopLive(Array.isArray(d) && d.length > 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const links = shopLive
    ? [...baseLinks, { to: "/shop", label: "Shop", id: "shop" }]
    : baseLinks;

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "border-b border-[#222226] bg-[#0A0A0B]/85 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 sm:px-8 lg:px-12">
          <Link
            to="/"
            data-testid="nav-logo"
            className="group flex items-center gap-3 text-white"
          >
            <LogoMark className="h-8 w-8 transition-transform duration-500 group-hover:rotate-[360deg]" />
            <span className="leading-none">
              <span className="block font-serif text-lg tracking-[0.18em] text-white">
                ADD VALUE
              </span>
              <span className="mt-1 block font-mono text-[9px] uppercase tracking-[0.4em] text-[#D4AF37]">
                Holdings LLC
              </span>
            </span>
          </Link>

          <nav
            className="hidden items-center gap-9 lg:flex"
            aria-label="Primary"
          >
            {links.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                data-testid={`nav-link-${l.id}`}
                className={({ isActive }) =>
                  `link-underline font-mono text-[11px] uppercase tracking-[0.25em] transition-colors duration-300 ${
                    l.id === "shop"
                      ? "border border-[#D4AF37]/50 px-4 py-2 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"
                      : isActive
                        ? "text-[#D4AF37]"
                        : "text-stone-300 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <button
            data-testid="nav-menu-button"
            onClick={() => setOpen(!open)}
            className="flex h-11 w-11 items-center justify-center border border-[#2a2a2e] text-white transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37] lg:hidden"
            aria-label="Toggle navigation menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-nav-panel"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-[#0A0A0B]/97 backdrop-blur-2xl lg:hidden"
          >
            <nav className="mx-auto w-full max-w-7xl px-6 sm:px-8">
              {links.map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.id}`}
                    className={({ isActive }) =>
                      `group flex items-baseline gap-4 border-b border-[#1c1c1f] py-5 ${
                        isActive ? "text-[#D4AF37]" : "text-white"
                      }`
                    }
                  >
                    <span className="font-mono text-[10px] tracking-[0.3em] text-[#D4AF37]/70">
                      0{i + 1}
                    </span>
                    <span className="font-serif text-4xl tracking-tight transition-colors duration-300 group-hover:text-[#D4AF37]">
                      {l.label}
                    </span>
                  </NavLink>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
